/**
 * 6.6 组件 Widget 注册表
 *
 * 维护 widgetClass 名称 -> BaseWidget 子类的映射（ComponentMaterial.widgetClass 引用）。
 * 每个组件的 Widget 类在对应分类文件中定义，并在本模块加载时注册。
 */
import type { PropertyPaneConfig } from "@shared/types";

import { getMaterial } from "../materials";
import type { BaseWidget, WidgetConstructor } from "./base-widget";

// 类型与辅助函数
export * from "./property-pane";
export * from "./property-types";
export * from "./binding-engine";
export * from "./base-widget";
export * from "./renderer-registry";

// 各分类 Widget 类
export * from "./layout-widgets";
export * from "./basic-widgets";
export * from "./data-widgets";
export * from "./ai-widgets";
export * from "./chart-widgets";
export * from "./content-widgets";
export * from "./motion-widgets";

import {
  PageWidget,
  ContainerWidget,
  CardWidget,
  GridWidget,
  FlexWidget,
  StackWidget,
  TabsWidget,
  SidebarWidget,
} from "./layout-widgets";
import {
  TextWidget,
  ButtonWidget,
  InputWidget,
  TextareaWidget,
  SelectWidget,
  SwitchWidget,
  CheckboxWidget,
  RadioWidget,
  DatePickerWidget,
  SliderWidget,
} from "./basic-widgets";
import {
  TableWidget,
  FormWidget,
  ListWidget,
  PaginationWidget,
  SearchBarWidget,
  FilterPanelWidget,
} from "./data-widgets";
import { AIChatWidget, AICopilotWidget, AIGenerateWidget } from "./ai-widgets";
import {
  ChartWidget,
  BarChartWidget,
  LineChartWidget,
  PieChartWidget,
  AreaChartWidget,
  MetricCardWidget,
} from "./chart-widgets";
import {
  RichTextWidget,
  FileUploadWidget,
  ImageWidget,
  VideoWidget,
  MarkdownWidget,
} from "./content-widgets";
import { MotionGroupWidget, AnimatedRouteWidget, ScrollRevealWidget } from "./motion-widgets";

/** widgetClass 名称 -> Widget 构造器 */
const widgetClasses: Record<string, WidgetConstructor> = {};

export function registerWidgetClass(name: string, ctor: WidgetConstructor): void {
  widgetClasses[name] = ctor;
}

export function getWidgetClass(name?: string): WidgetConstructor | undefined {
  if (!name) return undefined;
  return widgetClasses[name];
}

export function hasWidgetClass(name?: string): boolean {
  return Boolean(name && name in widgetClasses);
}

/** 按组件类型解析 Widget 实例（经由 material.widgetClass） */
export function getWidgetForType(type: string): BaseWidget | undefined {
  const material = getMaterial(type);
  if (!material?.widgetClass) return undefined;
  const ctor = getWidgetClass(material.widgetClass);
  return ctor ? new ctor() : undefined;
}

/** 按组件类型取属性面板配置（Widget 声明优先） */
export function getWidgetPropertyPaneConfig(type: string): PropertyPaneConfig[] | undefined {
  return getWidgetForType(type)?.getPropertyPaneConfig();
}

const ALL_WIDGET_CLASSES: WidgetConstructor[] = [
  // layout
  PageWidget,
  ContainerWidget,
  CardWidget,
  GridWidget,
  FlexWidget,
  StackWidget,
  TabsWidget,
  SidebarWidget,
  // basic
  TextWidget,
  ButtonWidget,
  InputWidget,
  TextareaWidget,
  SelectWidget,
  SwitchWidget,
  CheckboxWidget,
  RadioWidget,
  DatePickerWidget,
  SliderWidget,
  // data
  TableWidget,
  FormWidget,
  ListWidget,
  PaginationWidget,
  SearchBarWidget,
  FilterPanelWidget,
  // ai
  AIChatWidget,
  AICopilotWidget,
  AIGenerateWidget,
  // chart
  ChartWidget,
  BarChartWidget,
  LineChartWidget,
  PieChartWidget,
  AreaChartWidget,
  MetricCardWidget,
  // content
  RichTextWidget,
  FileUploadWidget,
  ImageWidget,
  VideoWidget,
  MarkdownWidget,
  // motion
  MotionGroupWidget,
  AnimatedRouteWidget,
  ScrollRevealWidget,
];

for (const ctor of ALL_WIDGET_CLASSES) {
  registerWidgetClass(ctor.name, ctor);
}
